/**
 * OperationsRoadmap Dialogs — Create and Detail dialogs
 */
import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Plus, Loader2, Trash2, User } from 'lucide-react';

export const CreateRoadmapDialog = ({
  open, onOpenChange, formData, setFormData, onSubmit, saving,
  CATEGORY_CONFIG, PRIORITY_CONFIG, STATUS_CONFIG
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
      <DialogHeader>
        <DialogTitle>Add Roadmap Item</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Title *</Label>
          <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="bg-slate-700 border-slate-600" placeholder="Feature or enhancement title" />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="bg-slate-700 border-slate-600 min-h-[100px]" placeholder="Detailed description, requirements, and context..." />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Category</Label>
            <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
              <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
              <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Effort Estimate</Label>
            <Select value={formData.estimated_effort} onValueChange={(v) => setFormData({...formData, estimated_effort: v})}>
              <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="small">Small (hours)</SelectItem>
                <SelectItem value="medium">Medium (days)</SelectItem>
                <SelectItem value="large">Large (weeks)</SelectItem>
                <SelectItem value="epic">Epic (months)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Target Quarter</Label>
            <Input value={formData.target_quarter} onChange={(e) => setFormData({...formData, target_quarter: e.target.value})} className="bg-slate-700 border-slate-600" placeholder="e.g., Q2 2026" />
          </div>
        </div>
        <div>
          <Label>Status</Label>
          <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
            <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          Create
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const DetailRoadmapDialog = ({
  open, onOpenChange, item, newNote, setNewNote,
  onStatusChange, onAddNote, onDelete, saving,
  PRIORITY_CONFIG, STATUS_CONFIG, CATEGORY_CONFIG
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
      {item && (
        <>
          <DialogHeader>
            <DialogTitle className="text-xl">{item.title}</DialogTitle>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={`${PRIORITY_CONFIG[item.priority]?.color} text-white`}>{item.priority.toUpperCase()}</Badge>
              <Badge className={`${STATUS_CONFIG[item.status]?.color} text-white`}>{STATUS_CONFIG[item.status]?.label}</Badge>
              <Badge variant="outline" className="border-slate-600 text-slate-300">{CATEGORY_CONFIG[item.category]?.label}</Badge>
            </div>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <h4 className="text-sm font-medium text-slate-400 mb-2">Description</h4>
              <div className="text-white whitespace-pre-wrap bg-slate-700/50 p-3 rounded-lg">{item.description || 'No description provided'}</div>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {item.estimated_effort && <div><span className="text-slate-400">Effort:</span><span className="text-white ml-2">{item.estimated_effort}</span></div>}
              {item.target_quarter && <div><span className="text-slate-400">Target:</span><span className="text-white ml-2">{item.target_quarter}</span></div>}
              <div><span className="text-slate-400">Created by:</span><span className="text-white ml-2">{item.created_by_name}</span></div>
              <div><span className="text-slate-400">Created:</span><span className="text-white ml-2">{new Date(item.created_at).toLocaleDateString()}</span></div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-400 mb-2">Change Status</h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <Button key={key} size="sm" variant={item.status === key ? "default" : "outline"} className={item.status === key ? cfg.color : "border-slate-600"} onClick={() => onStatusChange(item.id, key)}>
                    {cfg.label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-slate-400 mb-2">Notes ({item.notes?.length || 0})</h4>
              <div className="space-y-3 mb-3">
                {item.notes?.map(note => (
                  <div key={note.id} className="bg-slate-700/50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                      <User className="w-3 h-3" /> {note.author_name} <span>&middot;</span> {new Date(note.created_at).toLocaleString()}
                    </div>
                    <p className="text-white text-sm">{note.note}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Add a note..." className="bg-slate-700 border-slate-600" onKeyDown={(e) => e.key === 'Enter' && onAddNote()} />
                <Button onClick={onAddNote} disabled={saving || !newNote.trim()}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}</Button>
              </div>
            </div>
          </div>
          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button variant="destructive" size="sm" onClick={() => onDelete(item.id)}><Trash2 className="w-4 h-4 mr-2" /> Delete</Button>
            <DialogClose asChild><Button variant="outline">Close</Button></DialogClose>
          </DialogFooter>
        </>
      )}
    </DialogContent>
  </Dialog>
);
